<script>
	/* ------------ imports ------------ */
	import { loadStripe } from '@stripe/stripe-js';
	import { onMount, onDestroy } from 'svelte';
	import { browser } from '$app/environment';
	import { goto } from '$app/navigation';
	import { get } from 'svelte/store';

	import {
		cartContents,
		createOrUpdateCart,
		paymentIntentId,
		pointStore,
		theCart,
		usedDiscountCode,
		userCodeDiscount
	} from '$stores/cart';
	import { auth } from '$lib/auth';
	import { userStore } from '$stores/user';
	import { waitForPayment } from '$lib/payments/pollPayment';
	import { logError } from '$lib/helpers/errors';
	import posthog from 'posthog-js';

	/* ------------ props ------------ */
	export let instanceId;
	const elId = `express-${instanceId}`;

	/* ------------ PostHog flag helper ------------ */
	function flagAllowsUser() {
		if (!browser) return false;
		const FLAG = 'express_checkout_flag';
		if (!posthog.isFeatureEnabled?.(FLAG)) return false;

		const payload = posthog.getFeatureFlagPayload?.(FLAG);
		if (!payload) return true; // everyone

		let list;
		try {
			list = JSON.parse(payload);
		} catch {
			list = String(payload).split(','); // CSV
		}

		const email = (get(userStore).email || '').toLowerCase();
		const id = (posthog.get_distinct_id?.() || '').toLowerCase();

		return list.map((s) => s.trim().toLowerCase()).includes(email) || list.includes(id);
	}

	/* ------------ globals ------------ */
	const stripePromise = browser
		? loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY)
		: Promise.resolve(null);

	/* ------------ local state ------------ */
	let stripe, elements, btn;
	let overlay = false,
		oMsg = '',
		err = '';

	/* ------------ intent fetch ------------ */
	async function createIntent() {
		const cartId = await createOrUpdateCart();
		if (!cartId) throw new Error('No cart');

		// Build a lightweight “contents” array to send
		const contentsArray = get(cartContents).map((item) => ({
			id: item.id || item.item.id,
			quantity: item.quantity,
			price: item.price ?? item.cost,
			name: item.name,
			image: item.image || item.item?.featured_image || ''
		}));

		const body = {
			amount: get(theCart).intentTotal,
			customerID: get(auth)?.user?.uid,
			cartId,
			codeDiscount:
				get(usedDiscountCode) || get(theCart).userDiscount || get(userCodeDiscount) || '',
			pointsUsed: get(pointStore),
			customerEmail: get(userStore).email,
			userStoreData: {
				email: get(userStore).email,
				firstName: get(userStore).firstName,
				lastName: get(userStore).lastName
			},
			paymentIntentId: get(paymentIntentId),
			contents: contentsArray
		};

		const res = await fetch('/api/checkout/intent', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body)
		});
		if (!res.ok) throw new Error('Server error');
		return res.json(); // { client_secret }
	}

	/* ------------ mount / cleanup ------------ */
	let unsub;
	onMount(async () => {
		if (!browser) return;

		try {
			await refreshIntent(); // first render
		} catch (e) {
			err = e.message;
		}

		// refresh whenever the cart changes (debounced inside)
		unsub = cartContents.subscribe(() => refreshIntent());
	});

	onDestroy(() => {
		unsub?.();
		btn?.destroy?.();
	});

	/* ------------ intent refresh (debounced) ------------ */
	let timeout;
	async function refreshIntent() {
		clearTimeout(timeout);
		timeout = setTimeout(async () => {
			try {
				// Run Stripe load and intent creation in parallel
				const [{ client_secret }, s] = await Promise.all([createIntent(), stripePromise]);

				if (!client_secret || client_secret === 'noch' || !s) return;
				stripe = s;

				paymentIntentId.set(client_secret.split('_secret')[0]);

				elements = stripe.elements({ clientSecret: client_secret });
				btn?.destroy?.();
				btn = elements.create('expressCheckout', { buttonHeight: 40 });
				btn.mount(`#${elId}`);

				posthog.capture('express_checkout_loaded', {
					cartId: get(theCart).cartId,
					customerEmail: get(userStore).email,
					price: get(theCart).intentTotal,
					cartContentsLength: get(cartContents).length,
					pointsUsed: get(pointStore)
				});

				btn.on('confirm', handleConfirm);
			} catch (e) {
				err = e.message;

				posthog.capture('express_checkout_error', {
					error: err,
					customerEmail: get(userStore).email
				});
				overlay = false;
				logError(e, 'ExpressCheckout');
			}
		}, 400);
	}

	/* ------------ payment handler ------------ */
	async function handleConfirm() {
		if (!stripe || !elements) return;
		try {
			const { paymentIntent } = await stripe.confirmPayment({
				elements,
				redirect: 'if_required'
			});
			if (paymentIntent.status !== 'succeeded') return;

			overlay = true;
			oMsg = 'Processing…';

			const r = await waitForPayment(paymentIntent.id, 5_000);

			posthog.capture('order_added_express_checkout', {
				orderId: r.orderId,
				paymentIntentId: paymentIntent.id,
				customerEmail: get(userStore).email,
				price: get(theCart).intentTotal
			});

			goto(
				r.ok
					? `/checkout/step-three?orderId=${r.orderId}`
					: `/checkout/step-three` +
							`?payment_intent_id=${paymentIntent.id}` +
							`&payment_intent_client_secret=${paymentIntent.client_secret}`
			);
		} catch (e) {
			err = e.message;
			posthog.capture('express_checkout_error', {
				error: err,
				customerEmail: get(userStore).email
			});
			logError(e, 'ExpressCheckout');
		}
	}
</script>

<!-- ------------ markup ------------ -->
{#if !err}
	<div class="express-btn" id={elId}></div>
	<!-- {#if err}<p class="err">{err}</p>{/if} -->
	{#if overlay}
		<div class="overlay"><p>{oMsg}</p></div>
	{/if}
{/if}

<style>
	.express-btn {
		width: 100%;
		padding-top: 6px;
	}
	.err {
		color: #fe1100;
		text-align: center;
		font-weight: 600;
	}
	.overlay {
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(0, 0, 0, 0.7);
		color: #fff;
		z-index: 999;
	}
</style>
